<script setup lang="ts">
import { computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { 
	IonPage, 
	IonHeader, 
	IonToolbar, 
	IonTitle, 
	IonContent, 
	IonButtons, 
	IonBackButton,
	IonButton,
	IonSpinner,
	IonIcon
} from '@ionic/vue';
import { alertCircleOutline, chevronBackOutline } from 'ionicons/icons';

import BackButton from '@/shared/components/BackButton.vue';
import TopBar from '@/shared/components/TopBar.vue';
import UnsafeHtmlRenderer from '@/shared/components/UnsafeHtmlRenderer.vue';
import NavigationBar from '@/shared/components/ui/NavigationBar.vue';
import usePages from '@/shared/composables/usePages';
import { useSharedStore } from '@/stores/shared/sharedStore';
import { DynamicPage } from '@/shared/interfaces/dynamicPage';
import { useI18n } from 'vue-i18n';

// Route and router
const route = useRoute();
const router = useRouter();
const { t } = useI18n();

// Get page ID from route params
const pageId = computed(() => {
	const id = route.params.id;
	return typeof id === 'string' ? parseInt(id, 10) : null;
});

// Use shared store for pages data
const sharedStore = useSharedStore();
const { pages } = usePages();

const availablePages = computed<DynamicPage[]>(() => {
	if (pages.value && pages.value.length > 0) {
		return pages.value;
	}

	return sharedStore.pages || [];
});

// Computed properties
const page = computed<DynamicPage | undefined>(() => {
	if (!pageId.value) return undefined;
	return availablePages.value.find(p => p.id === pageId.value && p.type === 'html');
});

const isLoading = computed(() => availablePages.value.length === 0);
const error = computed(() => !page.value && availablePages.value.length > 0);

const pageTitle = computed(() => {
	if (isLoading.value) return t('pages.loading');
	if (error.value) return t('pages.error');
	if (page.value) return page.value.title || t('pages.title');
	return t('pages.not_found');
});

const showNavigationBar = computed(() => {
	return page.value?.display_mode === 'content';
});

// Methods
const goBack = () => {
	router.push('/dashboard');
};

const formatDate = (dateString: string): string => {
	try {
		return new Date(dateString).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'long',
			day: 'numeric'
		});
	} catch {
		return dateString;
	}
};
</script>

<template>
	<ion-content>
		<TopBar class="px-[34px]">
			<template #left>
				<BackButton to="/dashboard" />
			</template>

			<template #center>
				<div class="flex grow justify-center w-full font-sourcesanspro-semibold text-l2">
				{{ pageTitle }}
				</div>
			</template>

			<template #right>

			</template>
		</TopBar>

		
		<div v-if="isLoading" class="flex justify-center items-center h-full">
			<ion-spinner name="crescent"></ion-spinner>
		</div>

		<div v-else-if="error" class="flex flex-col justify-center items-center h-full p-4">
			<div class="bg-white border rounded-lg p-8 flex flex-col items-center">
				<h2 class="text-xl font-semibold text-gray-800 mb-2">{{ $t('pages.not_found') }}</h2>
				<p class="text-gray-600 text-center">
					{{ $t('pages.not_found_description') }}
				</p>
			</div>
		</div>

		<div v-else-if="page" class="px-[34px]">
			<UnsafeHtmlRenderer
				class="max-w-none"
				:html="typeof page.content === 'string' ? page.content : ''"
				:unsafe="Boolean(page.full_html)" />
		</div>

		<!-- Navigation Bar (only for 'content' display mode) -->
		<NavigationBar v-if="showNavigationBar" item-active="pages" />
	</ion-content>
</template>

<style scoped>
</style> 
